import { FeatureSet, NetworkEntity, TFeatureSets, TNetworkEntity } from '../../shared/typings/interfaces/network-sidenav.interface';
import { PartnerTypes, TValidPartnerTypes } from 'Src/ng2/shared/typings/interfaces/partner.interface';
import { INavItem } from './../../shell/side-nav/side-nav.config';
import { ISidebarItem } from './../../../nvps-libraries/design/nv-sidebar-list/nv-sidebar.interface';
import { ActivatedRoute } from '@angular/router';
import { TValidSchoolTypeCats } from 'Src/ng2/shared/typings/interfaces/school.interface';

export interface ISmallMenuNavItem {
  key: string;
  humanName: string;
  stateName?: string;
}

/*
  schoolTypes and districts are set to empty arrays because they are not being used for the Network side nav.
  Instead, we are using a 'feature_set' value sent by Data to determine which features a user has access to
*/
/**
 * NavItem Required Fields
 * key: unique key for the nav item
 * humanName: display name for the nav item
 * url: the url to navigate to when the item is clicked
 * order: the order of the item in the list
 * isActive: whether the item is active or not
 * schoolTypes: an object with a 'default' key that contains an array of school types that the item is available for
 * districts: an array of district ids that the item is available for
 * featureSet: an array of feature sets that the item is available for (if not provided, the item is available for all feature sets)
 * entityType: an array of entity types that the item is available for (if not provided, the item is available for all entity types)
 * children: an array of child NavItems (for accordion menus)
 * hasBetaFlag: whether to show a beta flag next to the item
 * leftDefaultIcon: the icon to display when the item is not selected
 * leftSelectedIcon: the icon to display when the item is selected
 * queryParams: an object or function that returns an object of query params to include in the url
 * isExternalUrl: whether the url is an external link (opens in new tab)
 */
export const TopSectionSidebarOptions: INavItem[] = [
  {
    key: 'NETWORK_DASHBOARD',
    humanName: 'Home',
    url: '../dashboard',
    order: 0,
    isActive: true,
    schoolTypes: { default: [] },
    districts: [],
    featureSet: [FeatureSet.FULL_SET, FeatureSet.PARTIAL_1, FeatureSet.PARTIAL_2, FeatureSet.PARTIAL_3],
    leftDefaultIcon: 'home-default',
    leftSelectedIcon: 'home-selected',
  },
  {
    key: 'NETWORK_GRID',
    humanName: 'Data Grid',
    url: '../grid',
    order: 1,
    isActive: true,
    schoolTypes: { default: [] },
    districts: [],
    featureSet: [FeatureSet.FULL_SET, FeatureSet.PARTIAL_1, FeatureSet.PARTIAL_2],
    leftDefaultIcon: 'data-grid-default',
    leftSelectedIcon: 'data-grid-selected',
  },
  {
    key: 'SCHOOL_PROFILE',
    humanName: 'School Profile',
    url: '../school-profile',
    order: 2,
    isActive: true,
    schoolTypes: { default: [] },
    districts: [],
    featureSet: [FeatureSet.FULL_SET, FeatureSet.PARTIAL_1, FeatureSet.PARTIAL_2, FeatureSet.PARTIAL_3],
    entityType: [NetworkEntity.NYC_SCHOOL, NetworkEntity.ECFIK],
    leftDefaultIcon: 'apple-grey',
    leftSelectedIcon: 'apple-blue',
  },
  {
    key: 'ALERTS',
    humanName: 'Alerts',
    url: '../midlevel',
    order: 3,
    isActive: true,
    schoolTypes: { default: ['hs', 'transfer'] },
    districts: [],
    entityType: [NetworkEntity.NYC_SCHOOL],
    leftDefaultIcon: 'op-nav-bell-grey',
    leftSelectedIcon: 'op-nav-bell-blue',
    queryParams: {
      selectedCategory: 'ALERTS',
    },
  },
];

export const MiddleSectionSidebarOptions: INavItem[] = [
  {
    key: 'ACADEMICS',
    humanName: 'Academics',
    url: '',
    order: 1,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.NYC_SCHOOL],
    queryParams: {
      selectedCategory: 'ACADEMICS',
    },
    children: [
      {
        key: 'ACADEMICS_BREAKDOWN',
        humanName: 'Breakdown',
        url: '../midlevel',
        order: 0,
        isActive: true,
        schoolTypes: { default: ['all'] },
        districts: [],
        entityType: [NetworkEntity.NYC_SCHOOL],
        queryParams: {
          selectedCategory: 'ACADEMICS',
        },
      },
      {
        key: 'PASS_RATES',
        humanName: 'Pass Rates',
        order: 1,
        isActive: true,
        schoolTypes: { default: ['ms', 'hs', 'transfer'] },
        districts: [],
        entityType: [NetworkEntity.NYC_SCHOOL],
        featureSet: [FeatureSet.FULL_SET, FeatureSet.PARTIAL_1],
        url: '../pass-rates',
        isExternalUrl: false,
      },
    ],
  },
  {
    key: 'ECFIK_ELIGIBLE_STUDENTS',
    humanName: 'Eligible Students',
    url: '../midlevel',
    order: 0,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.ECFIK],
    queryParams: {
      selectedCategory: 'ECFIK_ELIGIBLE_STUDENTS',
    },
  },
  {
    key: 'ATTENDANCE',
    humanName: 'Attendance',
    url: '',
    order: 2,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.NYC_SCHOOL, NetworkEntity.ECFIK, NetworkEntity.LANSING, NetworkEntity.NYC_SHELTER],
    queryParams: {
      selectedCategory: 'ATTENDANCE',
    },
    children: [
      {
        key: 'ATTENDANCE_BREAKDOWN',
        humanName: 'Breakdown',
        url: '../midlevel',
        order: 0,
        isActive: true,
        schoolTypes: { default: ['all'] },
        districts: [],
        entityType: [NetworkEntity.NYC_SCHOOL, NetworkEntity.ECFIK, NetworkEntity.LANSING, NetworkEntity.NYC_SHELTER],
        queryParams: {
          selectedCategory: 'ATTENDANCE',
        },
      },
      {
        key: 'ATTENDANCE_CALENDAR',
        humanName: 'Attendance Calendar',
        order: 1,
        isActive: true,
        schoolTypes: { default: ['all'] },
        districts: [],
        entityType: [NetworkEntity.NYC_SCHOOL, NetworkEntity.LANSING],
        featureSet: [FeatureSet.FULL_SET, FeatureSet.PARTIAL_1, FeatureSet.PARTIAL_2],
        queryParams: {
          selectedCategory: 'ATTENDANCE',
        },
        url: '../attendance-calendar',
        isExternalUrl: false,
      },
    ],
  },
  {
    key: 'ECFIK_PROGRAM_MONITORING',
    humanName: 'Program Monitoring',
    url: '../midlevel',
    order: 3,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.ECFIK],
    queryParams: {
      selectedCategory: 'ECFIK_PROGRAM_MONITORING',
    },
  },
  {
    key: 'COLLEGE_NOW',
    humanName: 'College Now',
    url: '../midlevel',
    order: 3,
    isActive: true,
    schoolTypes: { default: ['hs', 'transfer'] },
    districts: [],
    entityType: [NetworkEntity.NYC_SCHOOL],
    queryParams: {
      selectedCategory: 'COLLEGE_NOW',
    },
  },
  {
    key: 'CREDITS',
    humanName: 'Credits',
    url: '../midlevel',
    order: 4,
    isActive: true,
    schoolTypes: { default: ['hs', 'transfer'] },
    districts: [],
    entityType: [NetworkEntity.NYC_SCHOOL, NetworkEntity.LANSING],
    queryParams: {
      selectedCategory: 'CREDITS',
    },
  },
  {
    key: 'GRADUATION',
    humanName: 'Graduation',
    url: '../midlevel',
    order: 5,
    isActive: true,
    schoolTypes: { default: ['hs', 'transfer'] },
    districts: [],
    entityType: [NetworkEntity.NYC_SCHOOL],
    queryParams: {
      selectedCategory: 'GRADUATION',
    },
  },
  {
    key: 'POSTSECONDARY',
    humanName: 'Postsecondary',
    url: '',
    order: 6,
    isActive: true,
    schoolTypes: { default: ['hs', 'transfer'] },
    districts: [],
    entityType: [NetworkEntity.NYC_SCHOOL],
    queryParams: {
      selectedCategory: 'POSTSECONDARY',
    },
    children: [
      {
        key: 'POSTSECONDARY_DASHBOARD',
        humanName: 'Dashboard',
        url: '../postsecondary-dashboard',
        order: 0,
        isActive: true,
        schoolTypes: { default: ['hs', 'transfer'] },
        districts: [],
        entityType: [NetworkEntity.NYC_SCHOOL],
        queryParams: {
          selectedCategory: 'POSTSECONDARY',
        },
      },
      {
        key: 'KIMS_DASHBOARD',
        humanName: 'FRNYC KIMs tracker',
        url: '../kims-dashboard',
        order: 1,
        isActive: true,
        schoolTypes: { default: ['hs', 'transfer'] },
        districts: [],
        entityType: [NetworkEntity.NYC_SCHOOL],
        requiresKimsTracker: true,
        queryParams: {
          selectedCategory: 'KIMS',
        },
      },
    ],
  },
  {
    key: 'REGENTS',
    humanName: 'Regents',
    url: '',
    order: 7,
    isActive: true,
    schoolTypes: { default: ['hs', 'transfer'] },
    districts: [],
    entityType: [NetworkEntity.NYC_SCHOOL],
    queryParams: {
      selectedCategory: 'REGENTS',
    },
    children: [
      {
        key: 'REGENTS_BREAKDOWN',
        humanName: 'Breakdown',
        url: '../midlevel',
        order: 0,
        isActive: true,
        schoolTypes: { default: ['hs'] },
        districts: [],
        entityType: [NetworkEntity.NYC_SCHOOL],
        queryParams: {
          selectedCategory: 'REGENTS',
        },
      },
      {
        key: 'REGENTS_RESULTS',
        humanName: 'Regents Results',
        order: 1,
        isActive: true,
        schoolTypes: { default: ['hs', 'transfer'] },
        districts: [],
        entityType: [NetworkEntity.NYC_SCHOOL],
        featureSet: [FeatureSet.FULL_SET, FeatureSet.PARTIAL_1],
        url: '../regents-results',
        isExternalUrl: false,
      },
    ],
  },
  {
    key: 'SCREENERS',
    humanName: 'Screeners',
    url: '../midlevel',
    order: 8,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.NYC_SCHOOL, NetworkEntity.LANSING],
    queryParams: {
      selectedCategory: 'SCREENERS',
    },
  },
  {
    key: 'STUDENTS',
    humanName: 'Students',
    url: '../midlevel',
    order: 9,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.NYC_SCHOOL, NetworkEntity.LANSING, NetworkEntity.NYC_SHELTER],
    queryParams: {
      selectedCategory: 'STUDENTS',
    },
  },
  {
    key: 'FAMILY_NEEDS',
    humanName: 'Family Needs',
    url: '../midlevel',
    order: 8,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.ECFIK],
    children: [
      {
        key: 'FAMILY_NEEDS_BREAKDOWN',
        humanName: 'Breakdown',
        url: '../midlevel',
        order: 0,
        isActive: true,
        schoolTypes: { default: ['all'] },
        districts: [],
        entityType: [NetworkEntity.ECFIK],
        queryParams: {
          selectedCategory: 'FAMILY_NEEDS',
        },
      },
      {
        key: 'FAMILY_NEEDS_TRENDS',
        humanName: 'Trends',
        url: '../family-needs',
        order: 1,
        isActive: true,
        schoolTypes: { default: ['all'] },
        districts: [],
        entityType: [NetworkEntity.ECFIK],
        queryParams: {
          selectedCategory: 'NOTES_TRENDS',
        },
      },
    ],
  },
  // FRNYC Nav items
  {
    key: 'FRNYC_STUDENTS',
    humanName: 'Students',
    url: '../midlevel',
    order: 0,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.NYC_FUTURE_READY],
    queryParams: {
      selectedCategory: 'STUDENTS',
    },
  },
  {
    key: 'FRNYC_PATHWAYS',
    humanName: 'Pathways',
    url: '../midlevel',
    order: 1,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.NYC_FUTURE_READY],
    queryParams: {
      selectedCategory: 'FRNYC_PATHWAYS',
    },
  },
  {
    key: 'FRNYC_MILESTONES',
    humanName: 'Milestones',
    url: '../midlevel',
    order: 2,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.NYC_FUTURE_READY],
    queryParams: {
      selectedCategory: 'MILESTONES',
    },
  },
  {
    key: 'ATTENDANCE',
    humanName: 'Attendance',
    url: '../midlevel',
    order: 3,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.NYC_FUTURE_READY],
    queryParams: {
      selectedCategory: 'ATTENDANCE',
    },
  },
  {
    key: 'ACADEMICS',
    humanName: 'Academics',
    url: '../midlevel',
    order: 4,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.NYC_FUTURE_READY],
    queryParams: {
      selectedCategory: 'ACADEMICS',
    },
  },
  // CUNY Nav items
  {
    key: 'CUNY_ACADEMICS',
    humanName: 'Academics',
    url: '../midlevel',
    order: 0,
    isActive: true,
    schoolTypes: { default: ['hs'] },
    districts: [],
    entityType: [NetworkEntity.CUNY],
    queryParams: {
      selectedCategory: 'ACADEMICS',
    },
  },
  {
    key: 'CUNY_ATTENDANCE',
    humanName: 'Attendance',
    url: '../midlevel',
    order: 1,
    isActive: true,
    schoolTypes: { default: ['hs'] },
    districts: [],
    entityType: [NetworkEntity.CUNY],
    queryParams: {
      selectedCategory: 'ATTENDANCE',
    },
  },
  {
    key: 'CUNY_COLLEGE_NOW',
    humanName: 'College Now',
    url: '../midlevel',
    order: 2,
    isActive: true,
    schoolTypes: { default: ['hs', 'transfer'] },
    districts: [],
    entityType: [NetworkEntity.CUNY],
    queryParams: {
      selectedCategory: 'COLLEGE_NOW',
    },
  },
  {
    key: 'CUNY_FRNYC_PATHWAYS',
    humanName: 'FutureReadyNYC',
    url: '../midlevel',
    order: 3,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.CUNY],
    queryParams: {
      selectedCategory: 'FRNYC_PATHWAYS',
    },
  },
  {
    key: 'CUNY_POSTSECONDARY',
    humanName: 'Postsecondary',
    url: '../midlevel',
    order: 4,
    isActive: true,
    schoolTypes: { default: ['hs'] },
    districts: [],
    entityType: [NetworkEntity.CUNY],
    queryParams: {
      selectedCategory: 'POSTSECONDARY',
    },
  },
  {
    key: 'CUNY_STUDENTS',
    humanName: 'Students',
    url: '../midlevel',
    order: 5,
    isActive: true,
    schoolTypes: { default: ['all'] },
    districts: [],
    entityType: [NetworkEntity.CUNY],
    queryParams: {
      selectedCategory: 'STUDENTS',
    },
  },
];

/**
 * A function that filter Network features based on feature set field provided by data team
 * @param featureSet TFeatureSets
 * @param clusterId string
 * @param route ActivatedRoute
 * @returns ISidebarItem[] to be used for nv-sidebar-list
 */
export const getTopSectionSidebarOptions = ({
  featureSet,
  clusterId,
  route,
  userSchoolTypes,
  clusterType,
}:{
  featureSet: TFeatureSets,
  clusterId: string,
  route: ActivatedRoute,
  userSchoolTypes: TValidSchoolTypeCats[],
  clusterType: TNetworkEntity,
}): ISidebarItem[] => {
  const optionsCopy = structuredClone(TopSectionSidebarOptions);
  const filteredOptions = validate(optionsCopy, { featureSet, userSchoolTypes, clusterType });

  return shapeNavItems(filteredOptions, {
    contextPartnerId: clusterId,
    contextPartnerType: PartnerTypes.SCHOOL_NETWORK,
    route,
  });
};

/**
 * A function that filter content area options based on the user's entity type
 * and school types available in the user's portfolio
 * @param featureSet TFeatureSets
 * @param userSchoolTypes TValidSchoolTypeCats
 * @param clusterType TNetworkEntity
 * @param clusterId string
 * @param route ActivatedRoute
 * @param hasKimsTracker boolean whether the network has a FRNYC school; gates the FRNYC KIMs tracker item
 * @returns ISidebarItem[] to be used for nv-sidebar-list
 */
export const getMiddleSectionSidebarOptions = (
  featureSet: TFeatureSets,
  userSchoolTypes: TValidSchoolTypeCats[],
  clusterType: TNetworkEntity,
  clusterId: string,
  route: ActivatedRoute,
  hasKimsTracker: boolean = false,
): ISidebarItem[] => {
  const optionsCopy = structuredClone(MiddleSectionSidebarOptions);
  const filteredOptions = validate(optionsCopy, { userSchoolTypes, clusterType, featureSet, hasKimsTracker });

  return shapeNavItems(filteredOptions, {
    contextPartnerId: clusterId,
    contextPartnerType: PartnerTypes.SCHOOL_NETWORK,
    route,
  });
};

// helper functions
const isIncludedInFeatureSet = (navItem: INavItem, featureSet: TFeatureSets): boolean => {
  const hasNoFeatureSet = !navItem?.featureSet;
  return !featureSet || hasNoFeatureSet || navItem.featureSet.includes(featureSet);
};

export const hasCorrectSchoolType = (navItem: INavItem, userSchoolTypes: TValidSchoolTypeCats[] = []): boolean => {
  const { schoolTypes: { default: navItemSchoolTypes } } = navItem; // network uses feature_sets instead of districts, so always use default schoolTypes
  const hasNoSchoolTypes = !navItemSchoolTypes || navItemSchoolTypes.length === 0;
  const isAllSchoolType = navItemSchoolTypes.includes('all');
  const hasPermittedSchoolTypes = userSchoolTypes.some(schoolType => {
    return (navItemSchoolTypes.includes(schoolType.toLowerCase() as TValidSchoolTypeCats));
  });
  return hasPermittedSchoolTypes || isAllSchoolType || hasNoSchoolTypes;
};

const isAllowedForClusterType = (navItem: INavItem, clusterType: TNetworkEntity): boolean => {
  const hasNoEntityType = !navItem?.entityType || navItem?.entityType.length === 0;
  return !clusterType || hasNoEntityType || navItem?.entityType.includes(clusterType);
};

// The FRNYC KIMs tracker (requiresKimsTracker) is hidden for networks without a FRNYC school, per
// NetworkSideNavConfig.hasKimsTracker — its dashboard would have no data there. Items that don't
// require the KIMs tracker are unaffected.
const isAllowedForKimsTracker = (navItem: INavItem, hasKimsTracker: boolean = false): boolean => {
  return !navItem?.requiresKimsTracker || hasKimsTracker;
};

/**
 * Validates and filters the given NavItem options by checking a set of conditionals
 * @param options INavItem[]
 * @param opts contains various data that applies only if included within this object
 * @returns a filtered INavItem[]
 */
const validate = (
  options: INavItem[],
  opts: {
    userSchoolTypes?: TValidSchoolTypeCats[],
    featureSet?: TFeatureSets,
    clusterType?: TNetworkEntity,
    hasKimsTracker?: boolean,
  },
): INavItem[] => {
  const { userSchoolTypes, featureSet, clusterType, hasKimsTracker } = opts;
  const validOptions = options.filter((option: INavItem) => {
    if (option.children) {
      const validChildren = validate(option.children, opts);
      option.children = validChildren;
    }
    return (
      isIncludedInFeatureSet(option, featureSet) &&
      isAllowedForClusterType(option, clusterType) &&
      hasCorrectSchoolType(option, userSchoolTypes) &&
      isAllowedForKimsTracker(option, hasKimsTracker)
    );
  });
  return validOptions;
};

/**
 * A function that flattens a navItem with only 1 child unless skipUnwrap is set to true
 * @param item ISidebarItem
 * @returns ISidebarItem
 */
const unwrapSingleChildren = (item: ISidebarItem): ISidebarItem => {
  const { children, human, key, leftDefaultIcon, leftSelectedIcon, skipUnwrap } = item;
  if (skipUnwrap) return item;
  if (children?.length === 1) {
    return { ...children[0], human, key, leftDefaultIcon, leftSelectedIcon };
  } else {
    return item;
  }
};

export const shapeNavItems = (
  configs: INavItem[],
  opts: { contextPartnerId: string, contextPartnerType: TValidPartnerTypes, route: ActivatedRoute },
): ISidebarItem[] => {
  const { contextPartnerId, contextPartnerType, route } = opts;
  const shapedItems = configs.map(item => {
    const { humanName, key, url, queryParams, children, hasBetaFlag, leftDefaultIcon, leftSelectedIcon, queryParamsToHash } = item;
    const formattedChildren = children
      ? shapeNavItems(children, { contextPartnerId, contextPartnerType, route })
      : null;
    const expandAs = children ? ('accordion' as const) : null;
    const calculatedUrl = `${url}` || null;
    const calculatedQueryParams = typeof queryParams === 'function' ? queryParams(contextPartnerType, route.snapshot.queryParams) : queryParams;
    return {
      human: humanName,
      key: key || humanName,
      expandAs,
      children: formattedChildren,
      url: calculatedUrl,
      hasBetaFlag: hasBetaFlag || false,
      leftDefaultIcon,
      leftSelectedIcon,
      queryParams: calculatedQueryParams,
      queryParamsToHash,
      skipUnwrap: item.skipUnwrap,
    };
  });
  const unwrappedItems: ISidebarItem[] = shapedItems?.map(unwrapSingleChildren.bind(this));
  return unwrappedItems || [];
};
